import { useState, useEffect } from 'react';
import { useAuth } from '../components/auth/AuthProvider';
import { supabase } from '../lib/supabase';

export interface Device {
  id: string;
  parent_id: string;
  name: string;
  type: 'pc' | 'console' | 'mobile';
  operating_system: string;
  child_name: string;
  child_age: number;
  monitoring_enabled: boolean;
  status: 'online' | 'offline' | 'gaming' | 'paused';
  current_game?: string;
  total_game_time: number;
  alerts_count: number;
  risk_level: 'low' | 'medium' | 'high';
  created_at: string;
  updated_at: string;
}

export interface NewDevice {
  name: string;
  type: 'pc' | 'console' | 'mobile';
  operating_system: string;
  child_name: string;
  child_age: number;
}

export const useDevices = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchDevices = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Check if Supabase is properly configured
    if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
      console.error('Supabase environment variables not configured');
      setError('Database connection not configured. Please connect to Supabase.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('devices')
        .select('*')
        .eq('parent_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setDevices(data || []);
    } catch (err) {
      console.error('Error fetching devices:', err);
      
      // Handle different types of connection errors
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        setError('Unable to connect to database. Please ensure Supabase is properly configured.');
      } else if (err instanceof Error && err.message.includes('Invalid API key')) {
        setError('Database authentication failed. Please check your Supabase configuration.');
      } else if (err instanceof Error && err.message.includes('Network request failed')) {
        setError('Network connection failed. Please check your internet connection.');
      } else {
        setError('Database connection unavailable. Device features are temporarily disabled.');
      }
      
      // Set empty devices array to prevent UI crashes
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const addDevice = async (deviceData: NewDevice) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Validate device data before sending to server
    if (!deviceData.name?.trim() || deviceData.name.trim().length < 2) {
      const errorMessage = 'Device name must be at least 2 characters long';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    if (!deviceData.child_name?.trim() || deviceData.child_name.trim().length < 2) {
      const errorMessage = 'Child name must be at least 2 characters long';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
    try {
      setError(null);

      const { data, error: addError } = await supabase
        .from('devices')
        .insert({
          parent_id: user.id,
          name: deviceData.name.trim(),
          type: deviceData.type,
          operating_system: deviceData.operating_system,
          child_name: deviceData.child_name.trim(),
          child_age: deviceData.child_age,
        })
        .select()
        .single();

      if (addError) {
        throw addError;
      }

      setDevices(prev => [data, ...prev]);
      return { success: true, data };
    } catch (err) {
      console.error('Error adding device:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to add device';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const removeDevice = async (deviceId: string) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('devices')
        .delete()
        .eq('id', deviceId)
        .eq('parent_id', user.id);

      if (deleteError) {
        throw deleteError;
      }

      setDevices(prev => prev.filter(device => device.id !== deviceId));
      return { success: true };
    } catch (err) {
      console.error('Error removing device:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove device';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const updateDevice = async (deviceId: string, updates: Partial<Device>) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);

      const { data, error: updateError } = await supabase
        .from('devices')
        .update(updates)
        .eq('id', deviceId)
        .eq('parent_id', user.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      setDevices(prev => prev.map(device => 
        device.id === deviceId ? data : device
      ));
      return { success: true, data };
    } catch (err) {
      console.error('Error updating device:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update device';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [user]);

  return {
    devices,
    loading,
    error,
    addDevice,
    removeDevice,
    updateDevice,
    refreshDevices: fetchDevices,
  };
};